import { useEffect } from 'react';
import { useNode, type Node } from '@craftjs/core';
import { TEXT_NODE_NAME } from '../../utils/constants';
import {
  DEFAULT_VISIBILITY,
  getBorderRadiusStyle,
  getHeightStyle,
  getPaddingStyle,
  getPositionStyle,
  getTypographyStyle,
  getWidthStyle,
  getVisibilityClasses,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
  checkIsGradient,
} from '../../utils/styleHelpers';
import type { SlateEditorType } from '../../types/text';
import { SlateEditor } from './slateEditor/slateEditor';
import { useSlateTextStyles } from '../../hooks/useSlateTextStyles';
import { useRenderContext } from '../../context/RenderContext';
import { Element as SlateElement, type Descendant } from 'slate';
import { css, cx, injectGlobal } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { NodeCustomProps } from '../../types/craft';
import { createTypographyVariants } from '../../styles/typographyVariants';
import type { StylesConfig } from '../../types/styles';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';
import { TextPropsDto } from '../../dto/craft/props/TextPropsDto';

export type TextProps = TextPropsDto;

export const defaultProps: Partial<TextProps> = {
  maxWidth: 1200,
  maxWidthUnit: 'px',
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: DEFAULT_VISIBILITY,
  textAlign: 'left',
  shadow: 0,
  padding: [0, 0, 0, 0],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  position: 'relative',
  zIndex: 0,
  top: '',
  left: '',
  right: '',
  bottom: '',
  backgroundColor: undefined,
  lineHeight: 1.5,
  height: 'auto',
  width: '100%',
  fillSpace: 'yes',
  appliedGlobalStyle: true,
  selectedStyleVariant: 'heading.h2',
  textStyles: [
    {
      type: 'paragraph',
      children: [
        {
          selectedLinkVariant: undefined,
          text: 'Leadpages Builder',
          fontFamily: 'Inter',
          textTransform: 'none',
          letterSpacing: 0,
          fontSize: '18px',
        },
      ],
    },
  ],
  tablet: {},
  mobile: {},
};

const removeDefaultTextColor = (textStyles: Descendant[]) => {
  return textStyles.map((node) => {
    if (!SlateElement.isElement(node)) {
      return node;
    }
    const children = node.children.map((child) => {
      if (
        child &&
        typeof child === 'object' &&
        'color' in child &&
        child.color &&
        typeof child.color === 'object' &&
        !checkIsGradient(child.color) &&
        !('textSolid' in child) &&
        !('textGradient' in child) &&
        child.color.r === 0 &&
        child.color.g === 0 &&
        child.color.b === 0 &&
        child.color.a === 1
      ) {
        const rest = { ...child };
        delete rest.color;
        return rest;
      }
      return child;
    });
    return { ...node, children };
  });
};

const Text = ({
  onSlateEditorInit,
  onTextEdited,
  ...props
}: Partial<TextProps> & {
  onSlateEditorInit?: (id: string, editor: SlateEditorType) => void;
  onTextEdited?: () => void;
}) => {
  const mergedProps = {
    ...defaultProps,
    ...props,
  };

  const { visibility = DEFAULT_VISIBILITY, fillSpace } = mergedProps;

  const {
    id,
    isLocked,
    connectors: { connect, drag },
  } = useNode((node: Node) => ({
    isLocked: node.data.custom.isLocked,
  }));

  const rawTextStyles =
    typeof mergedProps.textStyles === 'string'
      ? JSON.parse(mergedProps.textStyles)
      : mergedProps.textStyles || [];
  const textStyles = mergedProps.appliedGlobalStyle
    ? removeDefaultTextColor(rawTextStyles)
    : rawTextStyles;

  const {
    isEditable,
    environment,
    fontManager,
    textVariants,
    styles: activeGlobalStyles,
  } = useRenderContext();

  const { value, handleChange, editor, fontVariants, resetOriginalText } =
    useSlateTextStyles(textStyles);

  const isServerRendering = environment === 'server';

  useEffect(() => {
    onSlateEditorInit?.(id, editor);
  }, [editor, id]);

  const handleBlur = () => {
    if (onTextEdited) {
      onTextEdited();
      resetOriginalText();
    }
  };

  if (!props.appliedGlobalStyle && isServerRendering && fontManager && fontVariants.length > 0) {
    fontVariants.forEach((variant) => {
      const fontFaceCss = fontManager.getFontFaceCss(
        variant.fontFamily,
        variant.fontWeight,
        variant.fontStyle,
      );
      if (fontFaceCss) {
        injectGlobal(fontFaceCss);
      }
    });
  }

  const visibilityStyleClasses = getVisibilityClasses(visibility);
  const isBuilderMode = environment === 'builder';
  const animationClass = getAnimationInitialClass(mergedProps.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(mergedProps);
  const disablePointerEvents = isEditable && isLocked;
  const isReadOnly = !isEditable || isLocked;

  return (
    <div
      className={cx(
        'text-node',
        `${fillSpace === 'yes' && 'flex-shrink-1-imp'}`,
        visibilityStyleClasses,
        animationClass,
        rootStyle(mergedProps, isServerRendering),
      )}
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      {...animationAttrs}
      {...(isBuilderMode ? { 'data-animation-preview': id } : {})}
    >
      <div
        className={cx(
          'slate-editor boundary-indicator',
          textContainerStyles(
            mergedProps,
            disablePointerEvents,
            isServerRendering,
            textVariants,
            activeGlobalStyles,
          ),
        )}
      >
        <SlateEditor
          editor={editor}
          value={value}
          onChange={handleChange}
          readOnly={isReadOnly}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
};

Text.craft = {
  displayName: TEXT_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: TEXT_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Text;

function rootStyle(props: Partial<TextProps>, isServerRendering: boolean) {
  const currentBpStyle = computeRootStyle({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
    width: props.width || '100%',
    height: props.height || 'auto',
  });
  const finalStyle: CSSInterpolation = {
    ...currentBpStyle,
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<TextProps>, props);
    const mobileStyles = computeRootStyle({
      ...mobile,
      ...positionProps,
    } as Partial<TextProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<TextProps>, props);
    const tabletStyles = computeRootStyle({
      ...tablet,
      ...positionProps,
    } as Partial<TextProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles;
  }
  return css(finalStyle);
}

function textContainerStyles(
  props: Partial<TextProps>,
  disablePointerEvents: boolean,
  isServerRendering: boolean,
  textVariants: ReturnType<typeof createTypographyVariants>,
  activeGlobalStyles: StylesConfig,
) {
  // Build CVA class when using global styles
  let typographyClass: string | undefined;

  if (props.appliedGlobalStyle && textVariants && props.selectedStyleVariant) {
    try {
      typographyClass = textVariants({
        variant: props.selectedStyleVariant,
      });
    } catch (error) {
      console.error('Error creating typography class:', error);
      // Fallback silently if variant is invalid
      typographyClass = undefined;
    }
  }

  const currentBpStyles = computeTextContainerStyle(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      borderRadius: props.borderRadius || [0, 0, 0, 0],
    },
    activeGlobalStyles,
  );

  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    overflow: 'hidden',
    outline: 'none',
    border: 'none',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    pointerEvents: disablePointerEvents ? 'none' : 'auto',
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeTextContainerStyle(
      mobile as Partial<TextProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeTextContainerStyle(
      tablet as Partial<TextProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles as CSSObject;
  }
  return cx(typographyClass, css(finalStyle));
}

function computeRootStyle(props: Partial<TextProps>) {
  const { margin, width, height, maxWidth, maxWidthUnit, fillSpace, zIndex } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const { hoverEffect, hoverTransitionDuration, hoverIntensity } = props;
  const marginStyle = margin ? getPaddingStyle(margin) : {};
  const widthStyle = width ? getWidthStyle(width) : {};
  const heightStyle = height ? getHeightStyle(height) : {};
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const flexStyle = fillSpace
    ? {
        flex: fillSpace === 'yes' ? `1 1 ${width}` : 'unset',
      }
    : {};
  // Use maxWidthUnit if provided, default to '%' for backward compatibility with existing data
  // New components will have maxWidthUnit: 'px' from defaultProps
  const unit = maxWidthUnit || '%';
  const maxWidthValue = maxWidth ? `${maxWidth}${unit}` : undefined;
  const hoverStyles = getHoverStyles(hoverEffect, hoverTransitionDuration, hoverIntensity);
  return {
    zIndex,
    ...marginStyle,
    ...widthStyle,
    ...heightStyle,
    ...positionStyle,
    maxWidth: maxWidthValue,
    ...flexStyle,
    ...hoverStyles,
  };
}

function computeTextContainerStyle(props: Partial<TextProps>, activeGlobalStyles: StylesConfig) {
  const {
    backgroundColor,
    lineHeight,
    padding,
    borderRadius,
    textAlign,
    shadow,
    appliedGlobalStyle,
  } = props;
  const paddingStyle = padding ? getPaddingStyle(padding) : {};
  const borderRadiusStyle = borderRadius ? getBorderRadiusStyle(borderRadius) : {};
  const typographyStyle = getTypographyStyle({
    shadow,
  }); //need to remove this function all over the place as it only return textShadow and textAlign
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);

  return {
    ...paddingStyle,
    ...borderRadiusStyle,
    ...typographyStyle,
    textAlign,
    ...(!appliedGlobalStyle
      ? {
          background, //part of global style
          lineHeight, //part of global style
        }
      : {}),
  };
}
